/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import styled from 'styled-components';

import { BUTTON_TITLE_TRANSLATION_KEYS } from '../../utils/constants';


const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 0 auto;

  @media only screen and (max-width: 700px) {
    flex-direction: column-reverse;
    justify-content: flex-start;
    gap: 16px;
    align-items: stretch;
  }
`;

function backClick({
  history,
  formPages,
  currentPageIndex,
  pathname
}: {
  history: any;
  formPages: any[];
  currentPageIndex: number;
  pathname: string;
}) {
  const url = pathname.replace(
    formPages[currentPageIndex].route,
    formPages[currentPageIndex - 1].route
  );
  history.push(url);
}

function progressClick({
  history,
  formPages,
  currentPageIndex,
  pathname,
  isReview,
  handleSubmit
}: {
  history: any;
  formPages: any[];
  currentPageIndex: number;
  pathname: string;
  isReview?: boolean;
  handleSubmit: () => void;
}) {
  if (isReview) {
    handleSubmit();
  } else {
    const url = pathname.replace(
      formPages[currentPageIndex].route,
      formPages[currentPageIndex + 1].route
    );
    history.push(url);
  }
}

function getProgressTitle({
  formPages,
  currentPageIndex,
  isReview,
  t
}: {
  formPages: any[];
  currentPageIndex: number;
  isReview?: boolean;
  t: (key: string) => string;
}) {
  const noOfPages = formPages.length - 1;
  const isLastFormPage = currentPageIndex === noOfPages - 1;

  if (isLastFormPage) {
    return t(BUTTON_TITLE_TRANSLATION_KEYS.review);
  } else if (isReview) {
    return t(BUTTON_TITLE_TRANSLATION_KEYS.submit);
  } else {
    return t(BUTTON_TITLE_TRANSLATION_KEYS.next);
  }
}

interface FormButtonNavigationProps {
  history: any;
  formPages?: any[];
  handleSubmit: () => void;
  disableProgress?: boolean;
  isReview?: boolean;
}

function FormButtonNavigation({
  history,
  formPages = [],
  handleSubmit,
  disableProgress,
  isReview
}: FormButtonNavigationProps): JSX.Element {
  const { pathname } = history.location;
  const { t } = useTranslation();

  const baseButtonSx = {
    display: 'flex',
    padding:
      'var(--padding-padding-base, 8px) var(--padding-padding-lg, 16px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--spacing-spacing-base, 8px)',
    borderRadius: 'var(--corner-radius-base, 8px)',
    textTransform: 'none',
    font: 'var(--sprout-font-body1-bold) !important',
    minWidth: { xs: '100%', md: '200px' },
    width: { xs: '100%', md: 'auto' },
    maxWidth: '100%',
    height: '40px !important'
  };

  const backButtonSx = {
    ...baseButtonSx,
    borderColor: 'var(--brand-leaf-green)',
    color: 'var(--brand-leaf-green)',
    background: '#fff'
  };

  const progressButtonSx = {
    ...baseButtonSx,
    background: 'var(--brand-leaf-green)',
    color: '#fff',
    '&:hover': {
      background: 'var(--brand-green-800)'
    }
  };

  const currentPageRoute =
    pathname.split('/').reverse()[0];

  const currentPageIndex = formPages.findIndex(
    page => page.route === currentPageRoute
  );

  return (
    <ButtonWrapper>
      {currentPageIndex > 0 ? (
        <Button
          type="button"
          onClick={() =>
            backClick({
              history,
              formPages,
              pathname,
              currentPageIndex
            })
          }
          variant="outlined"
          sx={backButtonSx}
        >
          {t(BUTTON_TITLE_TRANSLATION_KEYS.back)}
        </Button>
      ) : (
        <div />
      )}

      <Button
        disabled={disableProgress}
        type={isReview ? 'submit' : 'button'}
        onClick={() => {
          if (!disableProgress) {
            progressClick({
              history,
              formPages,
              pathname,
              isReview,
              currentPageIndex,
              handleSubmit
            });
          }
        }}
        variant="contained"
        sx={progressButtonSx}
      >
        {getProgressTitle({
          formPages,
          currentPageIndex,
          isReview,
          t
        })}
      </Button>
    </ButtonWrapper>
  );
}

export default FormButtonNavigation;